import dayjs from 'dayjs';
import { t } from 'i18next';
import * as yup from 'yup';
import { ObjectSchema, StringSchema } from 'yup';

import { ICompanySettings } from 'auth/types';
import { testRequiredSchema } from 'core/schemas';
import { getEmptyErrorMessage, numberSchemaWithTransform } from 'core/yup';
import { OrderType, PricingStrategy, QuoteEntityType } from 'quotes/enums';

import { quotePartCreateOrderSchemaFields } from '../QuotePartCreateCustomerOrder/schema';

const defaultCompanySettings: ICompanySettings = {
  coreOrderIntegrationEnabled: false,
  ddpOrderIntegrationEnabled: false,
  verticalMarketRequired: 0,
};

interface IProps {
  entityType?: QuoteEntityType;
  companySettings?: ICompanySettings;
  isEdit?: boolean;
  isAlternateVersionEnabled?: boolean;
}

export const upsertCustomerQuoteSchema = ({
  entityType = QuoteEntityType.QUOTE,
  companySettings = defaultCompanySettings,
  isEdit = false,
  isAlternateVersionEnabled = false,
}: IProps) => {
  const extraFields: {
    orderType?: StringSchema;
    shippingMethod?: ObjectSchema<{
      id: StringSchema;
      name: StringSchema;
    }>;
    freightTerm?: ObjectSchema<{
      id: StringSchema;
      name: StringSchema;
    }>;
  } = {};

  const pricingDateSchema = yup
    .string()
    .nullable()
    .test('future-date', t('errorPricingDateMustBeFuture'), (value) => {
      if (!value) {
        return true;
      }

      const selectedDate = dayjs(value).startOf('day');
      const today = dayjs().startOf('day');

      return selectedDate.valueOf() > today.valueOf();
    });

  const pricingStrategySchema = isAlternateVersionEnabled
    ? yup
        .string()
        .label(t('labelPricingStrategy'))
        .oneOf(Object.values(PricingStrategy))
        .nullable()
        .required(getEmptyErrorMessage(t('labelPricingStrategy')))
    : yup.string().nullable();

  if (isAlternateVersionEnabled && QuoteEntityType.ORDER === entityType) {
    extraFields.orderType = yup
      .string()
      .label(t('labelOrderType'))
      .nullable()
      .oneOf(Object.values(OrderType))
      .test(...testRequiredSchema(companySettings.ddpOrderIntegrationEnabled, t('labelOrderType')));

    extraFields.shippingMethod = yup
      .object()
      .shape({
        id: yup.string().label(t('labelShippingMethod')).nullable(),
        name: yup.string().label(t('labelShippingMethod')).nullable(),
      })
      .nullable()
      .test(
        ...testRequiredSchema(companySettings.ddpOrderIntegrationEnabled, t('labelShippingMethod'))
      );

    extraFields.freightTerm = yup
      .object()
      .shape({
        id: yup.string().label(t('labelFreightTerm')).nullable(),
        name: yup.string().label(t('labelFreightTerm')).nullable(),
      })
      .nullable()
      .test(
        ...testRequiredSchema(companySettings.ddpOrderIntegrationEnabled, t('labelFreightTerm'))
      );
  }

  const baseSchema = yup.object().shape({
    project: yup
      .object()
      .label(t('labelProject'))
      .nullable()
      .test(...testRequiredSchema(!isEdit, t('labelProject'))),
    jobName: yup
      .string()
      .label(t('labelJobName'))
      .nullable()
      .test(...testRequiredSchema(!isEdit, t('labelJobName'))),
    contact: yup
      .object()
      .label(t('labelCustomerQuoteCustomer'))
      .nullable()
      .test(...testRequiredSchema(!isEdit, t('labelCustomerQuoteCustomer'))),
    shipToLocation: yup
      .object()
      .label(t('labelShipToLocation'))
      .nullable()
      .test(...testRequiredSchema(!isEdit, t('labelShipToLocation'))),
    billToLocation: yup
      .object()
      .label(t('labelBillToLocation'))
      .nullable()
      .test(...testRequiredSchema(!isEdit, t('labelBillToLocation'))),
    setBillToAsDefaultForContact: yup
      .boolean()
      .label(t('labelSetBillToAsDefaultForContact'))
      .nullable(),
    setShipToAsDefaultForContact: yup
      .boolean()
      .label(t('labelSetShipToAsDefaultForContact'))
      .nullable(),
    verticalMarket: yup
      .object()
      .label(t('labelVerticalMarket'))
      .nullable()
      .test(
        ...testRequiredSchema(
          Boolean(companySettings.verticalMarketRequired),
          t('labelVerticalMarket')
        )
      ),
    location: yup
      .object()
      .label(t(`label${entityType}Location`))
      .nullable()
      .test(...testRequiredSchema(!isEdit, t(`label${entityType}Location`))),
    shippingLocation: yup
      .object()
      .label(t('labelDefaultShippingLocation'))
      .nullable()
      .required(getEmptyErrorMessage(t('labelDefaultShippingLocation'))),
    pricingDate: pricingDateSchema,
    pricingStrategy: pricingStrategySchema,
    ...extraFields,
  });

  if (QuoteEntityType.ORDER === entityType) {
    const orderSchemaFields = yup
      .object()
      .shape(
        quotePartCreateOrderSchemaFields(
          companySettings.coreOrderIntegrationEnabled ||
            (isAlternateVersionEnabled && companySettings.ddpOrderIntegrationEnabled),
          isAlternateVersionEnabled,
          isEdit
        )
      );

    return baseSchema.concat(orderSchemaFields);
  }

  const quoteSchema = yup.object().shape({
    closeConfidence: numberSchemaWithTransform()
      .label(t('labelCloseConfidence'))
      .nullable()
      .min(0, t('errorCloseConfidenceOutOfBounds'))
      .max(100, t('errorCloseConfidenceOutOfBounds'))
      .required(),
    closeDate: yup.string().label(t('labelCloseDate')).nullable().required(),
    validSince: yup.string().label(t('labelValidSince')).nullable(),
    validUntil: yup.string().label(t('labelValidUntil')).nullable().required(),
  });

  return baseSchema.concat(quoteSchema);
};
